import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { ProviderConfig, Suggestion } from '../types';

interface ProviderState {
  providerConfig: ProviderConfig | null;
  showProviderSetup: boolean;
  isLoading: boolean;
  loadingStatus: string;
  suggestions: Suggestion[];

  setProviderConfig: (config: ProviderConfig | null) => void;
  setShowProviderSetup: (show: boolean) => void;
  setIsLoading: (loading: boolean) => void;
  setLoadingStatus: (status: string) => void;
  setSuggestions: (suggestions: Suggestion[]) => void;
}

const getEnvProviderConfig = (): ProviderConfig | null => {
  if (import.meta.env.VITE_ANTHROPIC_API_KEY) {
    return {
      provider: 'anthropic',
      apiKey: import.meta.env.VITE_ANTHROPIC_API_KEY as string,
      model: 'claude-sonnet-4-20250514',
      providerName: 'Anthropic Claude',
    };
  }
  if (import.meta.env.VITE_OPENAI_API_KEY) {
    return {
      provider: 'openai',
      apiKey: import.meta.env.VITE_OPENAI_API_KEY as string,
      model: 'gpt-4o',
      providerName: 'OpenAI GPT',
    };
  }
  if (import.meta.env.VITE_GEMINI_API_KEY) {
    return {
      provider: 'gemini',
      apiKey: import.meta.env.VITE_GEMINI_API_KEY as string,
      model: 'gemini-2.0-flash-exp',
      providerName: 'Google Gemini',
    };
  }
  return null;
};

const hasEnvKey = () =>
  !!(
    import.meta.env.VITE_ANTHROPIC_API_KEY ||
    import.meta.env.VITE_OPENAI_API_KEY ||
    import.meta.env.VITE_GEMINI_API_KEY
  );

export const useProviderStore = create<ProviderState>()(
  persist(
    (set) => ({
      providerConfig: getEnvProviderConfig(),
      showProviderSetup: !hasEnvKey(),
      isLoading: false,
      loadingStatus: '',
      suggestions: [],

      setProviderConfig: (providerConfig) => set({ providerConfig }),
      setShowProviderSetup: (showProviderSetup) => set({ showProviderSetup }),
      setIsLoading: (isLoading) => set({ isLoading }),
      setLoadingStatus: (loadingStatus) => set({ loadingStatus }),
      setSuggestions: (suggestions) => set({ suggestions }),
    }),
    {
      name: 'zustand_provider',
      partialize: (state) => {
        if (!state.providerConfig) return {};
        // Never persist env-loaded configs
        const fromEnv =
          (state.providerConfig.provider === 'anthropic' && import.meta.env.VITE_ANTHROPIC_API_KEY) ||
          (state.providerConfig.provider === 'openai' && import.meta.env.VITE_OPENAI_API_KEY) ||
          (state.providerConfig.provider === 'gemini' && import.meta.env.VITE_GEMINI_API_KEY);
        if (fromEnv) return {};
        // System provider: don't persist apiKey
        const { apiKey: _drop, ...rest } = state.providerConfig;
        return {
          providerConfig: state.providerConfig.provider === 'system' ? rest : state.providerConfig,
        };
      },
      merge: (persisted, current) => {
        const stored = persisted as Partial<ProviderState> | undefined;
        const envConfig = getEnvProviderConfig();
        return {
          ...current,
          providerConfig: envConfig ?? stored?.providerConfig ?? current.providerConfig,
          showProviderSetup: envConfig
            ? false
            : stored?.providerConfig
              ? false
              : current.showProviderSetup,
        };
      },
      // Migrate from legacy brainstormer_settings if zustand_provider doesn't exist yet
      storage: {
        getItem: (name) => {
          const stored = localStorage.getItem(name);
          if (stored) return JSON.parse(stored);
          // Migrate from legacy format
          try {
            const legacy = localStorage.getItem('brainstormer_settings');
            if (legacy) {
              const parsed = JSON.parse(legacy);
              if (parsed.providerConfig) {
                return {
                  state: { providerConfig: parsed.providerConfig },
                  version: 0,
                };
              }
            }
          } catch { /* ignore */ }
          return null;
        },
        setItem: (name, value) => localStorage.setItem(name, JSON.stringify(value)),
        removeItem: (name) => localStorage.removeItem(name),
      },
    }
  )
);
